import { observer } from "mobx-react-lite";
import React from "react";

import { useFormFieldId } from "../form/form-field-id-context";
import { BaseCheckbox } from "./base-checkbox";
import { FieldProps, useModelBinding } from "./utils";

interface Props<M extends object> extends FieldProps<M, boolean> {
  disabled?: boolean;
  label?: string;
  /** For checkboxes with no visible `label`, which would otherwise be unnamed. */
  ariaLabel?: string;
}

export const Checkbox = observer(function Checkbox<M extends object>({
  disabled,
  label,
  ariaLabel,
  ...restProps
}: Props<M>) {
  const formFieldId = useFormFieldId();
  const { value, onChange } = useModelBinding(restProps);
  const checked = value ?? false;

  const checkbox = (
    <BaseCheckbox
      // The id a `FormRow`'s `<label>` points at, so its text toggles the box
      // as it focuses any other field - unless the box carries its own label,
      // as several in one row do, where one id would be on each of them.
      id={label ? undefined : formFieldId}
      checked={checked}
      onCheckedChange={(newChecked) => {
        onChange(newChecked === true);
      }}
      disabled={disabled}
      aria-label={ariaLabel}
    />
  );

  if (label) {
    return (
      <label className="inline-flex cursor-pointer items-center gap-1 has-disabled:cursor-not-allowed">
        <span>{label}</span>
        {checkbox}
      </label>
    );
  }

  return checkbox;
});
